import last from 'lodash/last'
import uniqBy from 'lodash/uniqBy'
import { useState, useEffect, Fragment, useRef } from 'react'
import { Link } from 'features/i18n/router'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import usePageModal from 'features/Modal/hooks/usePageModal'
import {
  LingerContextSchemaData,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import { User } from 'lib/_types'
import { useGetMembersQuery, endpoints } from 'store/api/yay/group'
import { useGroupUserRole } from 'pages/Group/hooks/useGroupUserRole'
import { MODAL_MODE } from './config'
import { useInfinityScrollSimple, useScroll } from 'hooks'

import Loading from 'components/Loading'
import LoadMore from 'components/LoadMore'
import { UserList } from 'components'

const GroupMembersModal = (props: {
  groupId: string
  userListProps: Object
  title: string
  onClose: Function
}) => {
  const { groupId, userListProps = {}, title, onClose } = props
  const ref = useRef<HTMLDivElement>(null)

  const location = useLocation()
  const { t } = useTranslation()
  const { generatePageModalUrl } = usePageModal()
  const { diff } = useScroll({ target: ref })

  const [__fromId, __fromIdSet] = useState<number>(null)

  const groupRes = endpoints?.getGroup?.useQueryState?.({
    groupId: groupId?.toString?.(),
  })

  const [__groupMembers, __groupMembersSet] = useState<User[]>([])

  const groupMembersRes = useGetMembersQuery({
    groupId,
    fromId: __fromId,
  })

  const loadMore = () => {
    __fromIdSet(last(groupMembersRes?.data)?.user?.id)
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  const groupUserRole = useGroupUserRole({ groupId })

  const onEnterViewport = (
    user: User,
    index: number,
    params: LingerContextSchemaData
  ) => {
    if (params.first_impression) {
      snowplowEvent.impression({
        user: userToUserContextSchemaData({ user, position: index + 1 }),
      })
    }
  }

  useEffect(() => {
    __groupMembersSet((prevState) => {
      const groupMembersData = (groupMembersRes?.data ?? []).map((x) => x.user)

      return uniqBy(
        [
          ...prevState.map(
            (x) => groupMembersData.find((y) => y.id == x.id) ?? x
          ),
          ...groupMembersData,
        ],
        (x) => x.id
      ) as User[]
    })
  }, [groupMembersRes])

  useEffect(() => {
    snowplowEvent.scroll({ diff })
  }, [diff])

  return (
    <div className="Modal__wrapper">
      <div className="Modal__header">
        <Link
          className="Modal__header__close"
          to={location.pathname}
          onClick={(e) => {
            if (typeof onClose !== 'undefined') {
              e.preventDefault()
              onClose()
            }
          }}
        />

        <h2 className="Modal__header__h2">
          {title ?? t('common.group_members')}
        </h2>
      </div>

      <div className="Modal__body" ref={ref}>
        {groupMembersRes?.isFetching && (__groupMembers?.length ?? 0) === 0 && (
          <Loading />
        )}

        {groupRes?.data?.pending_count > 0 &&
          (groupUserRole == 'moderator' || groupUserRole == 'owner') && (
            <Link
              to={generatePageModalUrl(MODAL_MODE.GROUP_JOIN_REQUEST, {
                groupId,
              })}
              className="Modal__group-members__join-request"
            >
              <span className="Modal__group-members__join-request__left">
                {t('group_member_modal.group_members_join_requests')}
              </span>

              <span className="Modal__group-members__join-request__right">
                {groupRes?.data?.pending_count}
              </span>
            </Link>
          )}

        {__groupMembers?.length > 0 && (
          <Fragment>
            <h3 className="Modal__group-members__header">
              {t('group_member_modal.owners.title')}
            </h3>

            <UserList
              users={(__groupMembers ?? []).filter(
                (x) => x?.id == groupRes?.data?.owner?.id
              )}
              followFrom="group_member_list"
              followCallbackSetter={__groupMembersSet}
              enableViewportCheck
              onEnterViewport={onEnterViewport}
              {...userListProps}
            />

            <h3 className="Modal__group-members__header">
              {t('group_member_modal.members.title')}
            </h3>

            <div className="ConfirmBox__users ConfirmBox__users--full">
              <UserList
                className="UserList--bd-b"
                users={(__groupMembers ?? []).filter(
                  (x) => x?.id != groupRes?.data?.owner?.id
                )}
                followFrom="group_member_list"
                followCallbackSetter={__groupMembersSet}
                enableViewportCheck
                onEnterViewport={(user, i, params) =>
                  onEnterViewport(user, i + 1, params)
                }
                {...userListProps}
              />

              <LoadMore
                isLoading={groupMembersRes?.isFetching}
                hidden={groupMembersRes?.data?.length == 0}
                onClick={() => {
                  loadMore()
                }}
                ref={loadMoreRef}
              />
            </div>
          </Fragment>
        )}
      </div>
    </div>
  )
}

export default GroupMembersModal
